import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, Bell, Check, Info, ArrowRight } from "lucide-react";

/**
 * A component to showcase the NannyPayPro brand theme colors and component styles.
 */
const ThemeDemo: React.FC = () => {
  return (
    <div className="w-full bg-background min-h-screen">
      {/* Hero Section with Primary color background */}
      <header className="bg-primary text-primary-foreground py-16 px-4 text-center mb-12">
        <h1 className="text-4xl font-extrabold tracking-tight mb-3">
          NANNY PAYROLL MADE EASY
        </h1>
        <p className="text-lg max-w-2xl mx-auto mb-6">
          The only HMRC recognised payroll software designed specifically for
          paying nannies. Run Payroll in 30 seconds!
        </p>
        <Button
          size="lg"
          className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
        >
          Get Started <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </header>

      <div className="max-w-5xl mx-auto p-8">
        {/* Color Palette Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-semibold mb-6 border-b border-primary/20 pb-2 text-primary">
            Color Palette
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            <ColorSwatch
              name="Primary"
              className="bg-primary text-primary-foreground"
              colorValue="#4573D2"
            />
            <ColorSwatch
              name="Secondary"
              className="bg-secondary text-secondary-foreground"
              colorValue="#F8F9FA"
            />
            <ColorSwatch
              name="Background"
              className="bg-background text-foreground border"
              colorValue="#F8F9FA"
            />
            <ColorSwatch
              name="Foreground"
              className="bg-foreground text-background border"
              colorValue="#1A202C"
            />
            <ColorSwatch
              name="Card"
              className="bg-card text-card-foreground border"
              colorValue="#FFFFFF"
            />
            <ColorSwatch
              name="Muted"
              className="bg-muted text-muted-foreground border"
              colorValue="#E2E8F0"
            />
            <ColorSwatch
              name="Accent"
              className="bg-accent text-accent-foreground border"
              colorValue="#F1F3F5"
            />
            <ColorSwatch
              name="Destructive"
              className="bg-destructive text-destructive-foreground"
              colorValue="#DC3545"
            />
          </div>
        </section>

        <Separator className="my-12 bg-primary/10" />

        {/* Buttons Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-semibold mb-6 border-b border-primary/20 pb-2 text-primary">
            Buttons
          </h2>
          <p className="text-muted-foreground mb-6">
            Main action buttons use the primary brand blue. Secondary actions
            use a light gray.
          </p>
          <div className="flex flex-wrap gap-4 items-center">
            <Button size="lg">Primary Large</Button>
            <Button>Primary Default</Button>
            <Button size="sm">Primary Small</Button>
            <Button variant="secondary">Secondary Action</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="link">Link Button</Button>
            <Button disabled>Disabled</Button>
          </div>
        </section>

        <Separator className="my-12 bg-primary/10" />

        {/* Cards Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-semibold mb-6 border-b border-primary/20 pb-2 text-primary">
            Cards
          </h2>
          <p className="text-muted-foreground mb-6">
            Cards group content on the standard white background.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-primary">Standard Card</CardTitle>
                <CardDescription>
                  Default card layout for content.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p>
                  Card content goes here. Use cards to organize information
                  clearly within the main background area.
                </p>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button variant="ghost">Learn More</Button>
                <Button>Action</Button>
              </CardFooter>
            </Card>

            {/* Card using secondary color for accent */}
            <Card className="border-secondary border-2 shadow-lg">
              <CardHeader>
                <CardTitle className="text-secondary">Accent Card</CardTitle>
                <CardDescription>
                  Card highlighted with the secondary action color.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p>
                  This card uses the secondary light gray color as a border accent
                  to draw attention or categorize specific information.
                </p>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button variant="outline">Details</Button>
                <Button variant="secondary">Confirm</Button>
              </CardFooter>
            </Card>
          </div>
        </section>

        <Separator className="my-12 bg-primary/10" />

        {/* Badges Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-semibold mb-6 border-b border-primary/20 pb-2 text-primary">
            Badges
          </h2>
          <p className="text-muted-foreground mb-6">
            Badges for status indicators.
          </p>
          <div className="flex flex-wrap gap-4 items-center">
            <Badge>Default (Primary)</Badge>
            <Badge variant="secondary">Info (Secondary)</Badge>
            <Badge variant="outline">Outline</Badge>
            <Badge variant="destructive">Error</Badge>
            <Badge className="bg-green-500 text-white hover:bg-green-600">
              Success
            </Badge>
          </div>
        </section>

        <Separator className="my-12 bg-primary/10" />

        {/* Alerts Section */}
        <section>
          <h2 className="text-3xl font-semibold mb-6 border-b border-primary/20 pb-2 text-primary">
            Alerts
          </h2>
          <p className="text-muted-foreground mb-6">
            Contextual feedback messages.
          </p>
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4 text-primary" />
              <AlertTitle>Informational Alert</AlertTitle>
              <AlertDescription>
                This is a standard informational alert using default card
                styling.
              </AlertDescription>
            </Alert>

            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                This alert indicates an error or problem.
              </AlertDescription>
            </Alert>

            {/* Custom Alert using Secondary color */}
            <Alert className="bg-secondary/10 border-secondary text-secondary-foreground">
              <Bell className="h-4 w-4 text-secondary" />
              <AlertTitle>Important Notice</AlertTitle>
              <AlertDescription>
                This alert uses the secondary color for emphasis.
              </AlertDescription>
            </Alert>

            <Alert className="bg-green-100 border-green-500 text-green-700">
              <Check className="h-4 w-4 text-green-500" />
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>
                Your action was completed successfully.
              </AlertDescription>
            </Alert>
          </div>
        </section>
      </div>
    </div>
  );
};

interface ColorSwatchProps {
  name: string;
  className: string;
  colorValue: string;
}

const ColorSwatch: React.FC<ColorSwatchProps> = ({
  name,
  className,
  colorValue,
}) => (
  <div className="flex flex-col items-center text-center group">
    <div
      className={`w-full rounded-lg h-28 shadow-md flex items-center justify-center mb-2 transition-transform duration-200 group-hover:scale-105 ${className}`}
    >
      <span className="font-bold text-sm">{name}</span>
    </div>
    <div className="text-xs">
      <p className="font-semibold text-foreground">{name}</p>
      <p className="text-muted-foreground font-mono">{colorValue}</p>
    </div>
  </div>
);

export default ThemeDemo;
